/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { forwardRef, InputHTMLAttributes, Ref } from 'react'
import {
  Checkbox,
  FormControl as MuiFormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'

import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      resetKey?: string

      onChange?: (
        _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | any
      ) => void
      onBlur?: (e: any) => void
    }
  >

const FormControl = forwardRef(
  (
    {
      id,
      type,
      label,
      checked,
      choices,
      placeholder,
      className,
      resetKey,
      children,
      error,
      onChange,
      onBlur,
      disabled,
      value,
      ...props
    }: FormControlProps,
    ref: Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    return (
      <div
        className={cn(className, {
          hidden: type === 'hidden'
        })}
      >
        {type === 'consent' ? (
          <FormControlLabel
            control={
              <Checkbox
                id={id}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                onBlur={onBlur}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.3rem'
                  }
                }}
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  ml: 1,
                  fontFamily: 'var(--font-helvetica)',
                  fontSize: '1.2rem',
                  lineHeight: '120%',
                  letterSpacing: '0.036rem',
                  '& a': {
                    textDecoration: 'underline'
                  },
                  ...(error && {
                    color: 'error.main'
                  })
                }}
              >
                {label}
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              ...(disabled && {
                pointerEvents: 'none',
                opacity: 0.4
              })
            }}
          />
        ) : type === 'select' ? (
          <MuiFormControl fullWidth error={error}>
            {label && <InputLabel id={`${id}-label`}>{label}</InputLabel>}
            <Select
              labelId={`${id}-label`}
              id={id}
              value={value || ''}
              label={label}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
              sx={{
                fontFamily: 'var(--font-helvetica)',
                fontSize: '1.2rem',
                lineHeight: '120%',
                letterSpacing: '0.036rem',
                borderRadius: '0.5rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.4)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.4)'
                }
              }}
            >
              {choices?.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.text}
                </MenuItem>
              ))}
            </Select>
          </MuiFormControl>
        ) : (
          <TextField
            id={id}
            type={type}
            label={label}
            placeholder={placeholder}
            multiline={type === 'textarea'}
            rows={type === 'textarea' ? 4 : undefined}
            error={error}
            disabled={disabled}
            fullWidth
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={ref}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'var(--font-helvetica)',
                fontSize: '1.2rem',
                lineHeight: '120%',
                letterSpacing: '0.036rem',
                borderRadius: '0.5rem',
                ...(type === 'textarea' && {
                  minHeight: '10.4rem'
                })
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.1)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.4)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.4)',
                  boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.2)'
                },
                ...(error && {
                  '& fieldset': {
                    borderColor: 'error.main'
                  },
                  '&.Mui-focused fieldset': {
                    boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.5)'
                  }
                }),
                ...(disabled && {
                  pointerEvents: 'none',
                  opacity: 0.4
                })
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'var(--font-helvetica)',
                fontSize: '1.2rem',
                letterSpacing: '0.036rem'
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'text.primary',
                opacity: 1
              }
            }}
          />
        )}
        {children}
      </div>
    )
  }
)

FormControl.displayName = 'FormControl'

export default FormControl
